import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../api/api";

// Componentes de Administração
import UserManagement from "../components/admin/UserManagement";
import CourseManagement from "../components/admin/CourseManagement";
import ProductManagement from "../components/admin/ProductManagement"; 
import SettingsManagement from "../components/admin/SettingsManagement";
import SliderManagement from "../components/admin/SliderManagement";
import CommunicationCenter from "../components/admin/CommunicationCenter";
import AdminEmailTemplates from "./AdminEmailTemplates";
import AdminPages from "../components/admin/AdminPages";
import PageBuilder from "../components/admin/PageBuilder";
import NotificationModal from "../components/common/NotificationModal";

// ⭐ Novo Componente de Monitoramento
import RealTimeMonitor from "../components/admin/RealTimeMonitor";

export default function AdminDashboard() {
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState("users");
  const [editingSlug, setEditingSlug] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [users, setUsers] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const [modal, setModal] = useState({
    show: false,
    type: "success",
    message: "",
  });

  const doRefresh = () => setRefresh((p) => p + 1);

  const showMessage = (type, message) =>
    setModal({ show: true, type, message });

  const closeMessage = () =>
    setModal((p) => ({ ...p, show: false }));

  /* ==============================
      BLOQUEIO DE ACESSO
  ============================== */
  if (!["admin", "supervisor"].includes(user?.role)) {
    return (
      <div className="p-16 text-center">
        <h2 className="text-2xl font-bold text-red-500">❌ Acesso negado</h2>
        <p className="opacity-70 mt-2">
          Apenas administradores podem acessar este painel.
        </p>
      </div>
    );
  }

  /* ==============================
      AÇÕES DE USUÁRIO
  ============================== */
  const handleUpdateStatus = async (userId, newStatus) => {
    try {
      await api.patch(`/admin/users/${userId}/status`, { status: newStatus });
      showMessage("success", "Status atualizado com sucesso!");
      doRefresh();
    } catch {
      showMessage("error", "Erro ao alterar status.");
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await api.patch(`/admin/users/${userId}/role`, { role: newRole });
      showMessage("success", "Perfil alterado!");
      doRefresh();
    } catch {
      showMessage("error", "Erro ao salvar novo perfil.");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Excluir este usuário?")) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      showMessage("success", "Usuário removido.");
      doRefresh();
    } catch {
      showMessage("error", "Erro ao excluir usuário.");
    }
  };

  useEffect(() => {
    async function loadUsers() {
      try {
        const res = await api.get("/auth/users");
        setUsers(res.data.users || []);
      } catch {
        showMessage("error", "Erro ao carregar usuários.");
      }
    }
    if (activeTab === "users") loadUsers();
  }, [refresh, activeTab]);

  /* ==============================
      COMPONENTE DE BOTÃO (NAV)
  ============================== */
  const NavButton = ({ id, label, icon }) => {
    const isActive = activeTab === id;
    return (
      <button
        onClick={() => {
          setActiveTab(id);
          setEditingSlug(null);
          if (window.innerWidth < 768) setIsSidebarOpen(false);
        }}
        className={`group relative flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-300 ${
          isActive 
            ? "bg-indigo-600 text-white shadow-md" 
            : "text-blue-900 hover:bg-indigo-50 hover:text-indigo-700"
        } ${!isSidebarOpen ? "justify-center px-0 w-12 mx-auto" : "w-full"}`}
      >
        <span className="text-xl">{icon}</span>
        {isSidebarOpen && <span className="whitespace-nowrap">{label}</span>}
        
        {/* Tooltip para sidebar fechada */}
        {!isSidebarOpen && (
          <span className="absolute left-14 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-[100]">
            {label}
          </span>
        )}
      </button>
    );
  };

  /* ==============================
      RENDERIZAÇÃO DO CONTEÚDO
  ============================== */
  const renderContent = () => {
    switch (activeTab) {
      case "realtime":
        return <RealTimeMonitor />;
        
      case "users":
        return (
          <UserManagement
            users={users}
            onUpdateStatus={handleUpdateStatus}
            onUpdateRole={handleUpdateRole}
            onDelete={handleDeleteUser}
          />
        );
        
      case "courses":
        return <CourseManagement refresh={refresh} doRefresh={doRefresh} />;
      
      case "shop":
        return (
          <ProductManagement 
            refresh={refresh} 
            doRefresh={doRefresh} 
            setNotification={(data) => showMessage(data.type, data.message)}
          />
        );

      case "settings":
        return <SettingsManagement refresh={refresh} doRefresh={doRefresh} />;
        
      case "pages":
        return editingSlug ? (
          <PageBuilder slug={editingSlug} />
        ) : (
          <AdminPages onEdit={setEditingSlug} />
        );
        
      case "slider":
        return (
          <SliderManagement 
            setNotification={(data) => showMessage(data.type, data.message)} 
          />
        );
        
      case "communications":
        return (
          <CommunicationCenter
            refresh={refresh}
            doRefresh={doRefresh}
            setNotification={(data) => showMessage(data.type, data.message)}
          />
        );
        
      case "emailTemplates":
        return <AdminEmailTemplates refresh={refresh} doRefresh={doRefresh} />;
        
      default:
        return (
          <div className="p-10 text-center text-gray-400">
            Selecione uma opção no menu lateral.
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* SIDEBAR */}
      <aside 
        className={`transition-all duration-300 border-r border-gray-200 bg-white fixed md:relative z-50 h-screen overflow-y-auto ${
          isSidebarOpen ? "w-64 p-4" : "w-20 p-4"
        } ${!isSidebarOpen ? "-translate-x-full md:translate-x-0" : "translate-x-0"}`}
      >
        <div className="flex items-center mb-6 justify-between">
          {isSidebarOpen && <h2 className="text-xl font-bold text-indigo-900 tracking-tight">⚙️ Painel</h2>}
          <button 
            onClick={() => setIsSidebarOpen(!isSidebarOpen)} 
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {isSidebarOpen ? "◀" : "▶"}
          </button>
        </div>

        <nav className="flex flex-col gap-2">
          {/* ⭐ Monitoramento em tempo real adicionado ao topo */}
          <NavButton id="realtime" label="Tempo Real" icon="⚡" />
          
          <div className="my-2 border-t border-gray-100"></div>
          
          <NavButton id="users" label="Usuários" icon="👥" />
          <NavButton id="courses" label="Cursos" icon="🎓" />
          <NavButton id="shop" label="Loja" icon="🛍️" />
          <NavButton id="settings" label="Configurações" icon="🌐" />
          <NavButton id="pages" label="Páginas" icon="🏗️" />
          <NavButton id="slider" label="Slider" icon="🖼️" />
          <NavButton id="communications" label="Comunicações" icon="✉️" />
          <NavButton id="emailTemplates" label="Emails" icon="📝" />
        </nav>
      </aside>

      {/* CONTEÚDO PRINCIPAL */}
      <main className="flex-1 p-4 md:p-8 overflow-x-hidden">
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-slate-800 italic">Administração</h1>
          <p className="text-gray-500">Gestão de plataforma, alunos e métricas em tempo real</p>
        </header>

        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 min-h-[500px]">
          {renderContent()}
        </section>
      </main>

      {/* MODAL DE NOTIFICAÇÃO */}
      <NotificationModal
        show={modal.show}
        type={modal.type}
        message={modal.message}
        onClose={closeMessage}
      />
    </div>
  );
}